/**
 * Employment rules, client side (WP-15 write screens).
 *
 * The authority is `onesphere/engines/employment.py`: the endpoint re-runs every rule and refuses the save if one
 * blocks, so nothing here can be bypassed by editing the page. This mirror exists so the person filling the form is
 * told *which field* is wrong, in their own language, before they submit — a server round trip that comes back with
 * one sentence is a worse way to learn that a temporary contract needs an end date.
 *
 * Keep the two in step: the group table and the codes below are the same table and the same codes, and
 * `employment.test.ts` pins the pairs that matter.
 */
export const PERMANENT = "permanent";
export const FIXED_TERM = "fixed_term";
export const TRAINEE = "trainee";
export const OTHER = "other";
export const UNSET = "unset";

/** Employment type → group. `Full-time` stays mapped: it is what 56 records carried before the customer's own words existed. */
export const TYPE_GROUP: Record<string, string> = {
  Permanent: PERMANENT,
  "Full-time": PERMANENT,
  Temporary: FIXED_TERM,
  Contract: FIXED_TERM,
  Probation: FIXED_TERM,
  Intern: TRAINEE,
  Apprentice: TRAINEE,
  "Part-time": OTHER,
  Commission: OTHER,
  Piecework: OTHER,
};

/** Groups whose whole point is that the engagement ends on a known date. */
export const ENDS_ON_A_DATE = [FIXED_TERM, TRAINEE];

export const STATUSES = ["Active", "Inactive", "Suspended", "Left"] as const;
export const CONTRACTOR_TYPES = ["Firm", "Individual"] as const;
export const CONTRACTOR_STATUSES = ["Active", "Suspended", "Blacklisted", "Closed"] as const;

export const MIN_AGE_AT_JOINING = 16;
export const MAX_AGE_AT_JOINING = 75;

export type Severity = "error" | "warning";
export type Problem = { field: string; code: string; severity: Severity; params?: Record<string, unknown> };
export type Values = Record<string, unknown>;

export function groupOf(employmentType: string | null | undefined): string {
  if (!employmentType) return UNSET;
  return TYPE_GROUP[employmentType] ?? OTHER;
}
export function endsOnADate(employmentType: string | null | undefined): boolean {
  return ENDS_ON_A_DATE.includes(groupOf(employmentType));
}

const str = (v: unknown): string => (typeof v === "string" ? v.trim() : v == null ? "" : String(v));
const problem = (field: string, code: string, severity: Severity = "error", params?: Record<string, unknown>): Problem =>
  ({ field, code, severity, ...(params ? { params } : {}) });

/** A date-only value; anything the browser's date input cannot produce comes back null rather than Invalid Date. */
export function asDate(value: unknown): Date | null {
  const s = str(value).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const d = new Date(`${s}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function looksLikeEmail(value: string): boolean {
  const at = value.indexOf("@");
  if (at < 1 || at !== value.lastIndexOf("@")) return false;
  const domain = value.slice(at + 1);
  return domain.includes(".") && !domain.startsWith(".") && !domain.endsWith(".");
}

function yearsBetween(earlier: Date, later: Date): number {
  let years = later.getFullYear() - earlier.getFullYear();
  const before = later.getMonth() < earlier.getMonth()
    || (later.getMonth() === earlier.getMonth() && later.getDate() < earlier.getDate());
  if (before) years -= 1;
  return years;
}

export function validateEmployee(values: Values, opts: { name?: string; descendants?: string[]; today?: Date } = {}): Problem[] {
  const out: Problem[] = [];
  const today = opts.today ?? new Date();
  const descendants = opts.descendants ?? [];

  for (const field of ["first_name", "company", "gender"]) {
    if (!str(values[field])) out.push(problem(field, "required"));
  }

  const joining = asDate(values.date_of_joining);
  const birth = asDate(values.date_of_birth);
  if (!str(values.date_of_joining)) out.push(problem("date_of_joining", "required"));
  else if (!joining) out.push(problem("date_of_joining", "not_a_date"));
  if (!str(values.date_of_birth)) out.push(problem("date_of_birth", "required"));
  else if (!birth) out.push(problem("date_of_birth", "not_a_date"));

  if (birth && birth >= today) out.push(problem("date_of_birth", "in_the_future"));
  if (birth && joining) {
    if (birth >= joining) out.push(problem("date_of_birth", "after_joining"));
    else {
      const age = yearsBetween(birth, joining);
      if (age < MIN_AGE_AT_JOINING) out.push(problem("date_of_birth", "very_young_at_joining", "warning", { age, floor: MIN_AGE_AT_JOINING }));
      else if (age > MAX_AGE_AT_JOINING) out.push(problem("date_of_birth", "very_old_at_joining", "warning", { age }));
    }
  }

  const status = str(values.status) || "Active";
  if (!(STATUSES as readonly string[]).includes(status)) {
    out.push(problem("status", "not_a_status", "error", { allowed: STATUSES.join(", ") }));
  }

  const employmentType = str(values.employment_type);
  const group = groupOf(employmentType);
  const end = asDate(values.contract_end_date);
  if (group === UNSET) out.push(problem("employment_type", "no_type", "warning"));
  if (ENDS_ON_A_DATE.includes(group) && !str(values.contract_end_date)) {
    out.push(problem("contract_end_date", "required_for_type", "error", { type: employmentType }));
  }
  if (group === PERMANENT && end) out.push(problem("contract_end_date", "permanent_with_an_end", "warning"));
  if (end && joining && end <= joining) out.push(problem("contract_end_date", "before_joining"));
  if (employmentType === "Probation" && !str(values.final_confirmation_date)) {
    out.push(problem("final_confirmation_date", "probation_without_confirmation", "warning"));
  }
  const confirmation = asDate(values.final_confirmation_date);
  if (confirmation && joining && confirmation <= joining) out.push(problem("final_confirmation_date", "before_joining"));

  const manager = str(values.reports_to);
  if (manager) {
    if (opts.name && manager === opts.name) out.push(problem("reports_to", "own_manager"));
    else if (descendants.includes(manager)) out.push(problem("reports_to", "reporting_cycle"));
  } else if (status === "Active") {
    out.push(problem("reports_to", "no_manager", "warning"));
  }

  for (const field of ["company_email", "personal_email"]) {
    const value = str(values[field]);
    if (value && !looksLikeEmail(value)) out.push(problem(field, "not_an_email"));
  }

  if (status === "Left") out.push(...validateEndEmployment(values, joining));

  return out.sort((a, b) => (a.severity === b.severity ? 0 : a.severity === "error" ? -1 : 1));
}

export function validateEndEmployment(values: Values, joining: Date | null = null): Problem[] {
  const out: Problem[] = [];
  const relieving = asDate(values.relieving_date);
  if (!str(values.relieving_date)) out.push(problem("relieving_date", "required_when_left"));
  else if (!relieving) out.push(problem("relieving_date", "not_a_date"));
  if (relieving && joining && relieving < joining) out.push(problem("relieving_date", "before_joining"));
  if (!str(values.reason_for_leaving)) out.push(problem("reason_for_leaving", "required_when_left"));
  return out;
}

export function validateContractor(values: Values): Problem[] {
  const out: Problem[] = [];
  if (!str(values.contractor_name)) out.push(problem("contractor_name", "required"));
  const kind = str(values.contractor_type);
  if (!kind) out.push(problem("contractor_type", "required"));
  else if (!(CONTRACTOR_TYPES as readonly string[]).includes(kind)) {
    out.push(problem("contractor_type", "not_a_contractor_type", "error", { allowed: CONTRACTOR_TYPES.join(", ") }));
  }
  const status = str(values.status) || "Active";
  if (!(CONTRACTOR_STATUSES as readonly string[]).includes(status)) {
    out.push(problem("status", "not_a_contractor_status", "error", { allowed: CONTRACTOR_STATUSES.join(", ") }));
  }
  const email = str(values.email);
  if (email && !looksLikeEmail(email)) out.push(problem("email", "not_an_email"));
  if (kind === "Firm" && !str(values.contact_person)) out.push(problem("contact_person", "firm_without_a_contact", "warning"));
  if (kind === "Individual" && !str(values.national_id)) out.push(problem("national_id", "individual_without_an_id", "warning"));
  return out.sort((a, b) => (a.severity === b.severity ? 0 : a.severity === "error" ? -1 : 1));
}

export const blocking = (problems: Problem[]): Problem[] => problems.filter((p) => p.severity === "error");
/** The problems on one field, worst first — what the input renders under itself. */
export const forField = (problems: Problem[], field: string): Problem[] => problems.filter((p) => p.field === field);
